import { planStitchedColumns } from './stitchedColumnPlan';
import type { StitchedColumnPlan } from './stitchedColumnPlan';
import type { StitchedColumns } from './StitchedColumns';
import type { StitchedColumnsFactory } from './stitchedColumnsFactory';
/**
 * The client-stitching stage of a symbol-key table (ADR 0020, DEC-ROLLOUT-001, DEC-ROLLOUT-002).
 *
 * A widget's composition root wires it between its column template state and its table: it takes
 * the column template and the server sort, and gives back the template the server table is built
 * from and the stitched columns the grid view binds. While `stitch` is false it passes the column
 * template through; while true it gives the keys columns and owns one `StitchedColumns`. The
 * table needs no knowledge of stitching: a changed template changes its query key, so TableModel
 * creates the other table and emits `NEW_TABLE_RESET`, and the grid rebinds to the new `columns`.
 */
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { ColumnOptions, SortModel } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import { getClientColumnDefinition } from 'src/features/clientColumns';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';

export interface SymbolTableStitchingInit {
    /** Whether the table stitches now; the root reads it from the rollout flag. */
    readonly stitch: ReadonlySignal<boolean>;
    readonly createColumns: StitchedColumnsFactory;
    readonly slugMap: ReadonlySignal<SlugMapDict>;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    /** The sort the server table applies; the keys table computes every sorted column. */
    readonly sort: ReadonlySignal<SortModel | undefined>;
    /**
     * Whether a sticky search is active. The keys table then carries every string column, so the
     * search matches what it matches on a full table (OD-SEARCH-001).
     */
    readonly searching: ReadonlySignal<boolean>;
    /**
     * Columns with an applied column filter. The keys table carries each of them, because its
     * filter references them (ADR 0014, AC-FILTER-008).
     */
    readonly filteredColumnIds: ReadonlySignal<ReadonlySet<string>>;
    readonly columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    readonly devtoolsName: string;
}

export interface SymbolTableStitchingShape {
    /** The stitched column plan; null while not stitching or while it cannot be planned yet. */
    readonly plan: ReadonlySignal<StitchedColumnPlan | null>;
    /**
     * The template the server table is built from: the column template, or its keys columns while
     * stitching. Null while there is no template, or while stitching cannot plan one yet.
     */
    readonly tableTemplate: ReadonlySignal<Pick<ColumnTemplate, 'columns'> | null>;
    /** The stitched columns to pass to the grid view while stitching. */
    readonly columns: ReadonlySignal<StitchedColumns | undefined>;
}

export const SymbolTableStitching: ModelConstructor<SymbolTableStitchingShape, [SymbolTableStitchingInit]> =
    createModel<SymbolTableStitchingShape, [SymbolTableStitchingInit]>((init: SymbolTableStitchingInit) => {
        const lifetime = new DisposableStack();
        effect(() => () => lifetime.dispose());

        // Memoized: an equal plan keeps its reference, so stitched bindings and the keys query see
        // no change.
        let lastPlan: StitchedColumnPlan | null = null;
        const plan: ReadonlySignal<StitchedColumnPlan | null> = computed(() => {
            const template = init.columnTemplate.value;
            const next =
                init.stitch.value && template
                    ? planStitchedColumns(template.columns, init.sort.value, init.slugMap.value, {
                          clientColumnSlugs: (clientKey) => getClientColumnDefinition(clientKey)?.requiredSlugs ?? [],
                          searching: init.searching.value,
                          filteredColumnIds: init.filteredColumnIds.value,
                      })
                    : null;
            if (!isEqual(next, lastPlan)) {
                lastPlan = next;
            }
            return lastPlan;
        });

        // Memoized like the plan: showing, hiding, or adding a stitched column replans without
        // changing the keys columns, and keeps the table template's reference.
        let lastKeysTemplate: Pick<ColumnTemplate, 'columns'> | null = null;
        const tableTemplate = computed((): Pick<ColumnTemplate, 'columns'> | null => {
            const template = init.columnTemplate.value;
            if (!init.stitch.value || !template) {
                return template;
            }
            const keysColumns = plan.value?.keysColumns;
            const next = keysColumns ? { columns: [...keysColumns] } : null;
            if (!isEqual(next, lastKeysTemplate)) {
                lastKeysTemplate = next;
            }
            return lastKeysTemplate;
        });

        // At most one StitchedColumns, replaced when stitching starts and released when it stops.
        const current = lifetime.use(new DisposableBox<StitchedColumns>());
        const columns = signal<StitchedColumns | undefined>(undefined);
        effect(() => {
            const stitch = init.stitch.value;
            untracked(() => {
                if (stitch) {
                    current.next(() =>
                        init.createColumns({
                            plan,
                            columnOptions: init.columnOptions,
                            devtoolsName: init.devtoolsName,
                        }),
                    );
                } else {
                    current.clear();
                }
                columns.value = current.current;
            });
        });

        return {
            plan,
            tableTemplate,
            columns,
        };
    });

export type SymbolTableStitching = Model<SymbolTableStitchingShape>;
